import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  CreditCard,
  Globe,
  LogOut,
  ScrollText,
  Settings2,
  UserRound,
  UsersRound,
} from "lucide-react";

import { DEFAULT_BRANDING, type Branding } from "@lagoon/config/branding";
import { parseConnections } from "@lagoon/config/connections";
import {
  AppLayout,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LagoonLockup,
  Mono,
  type NavItem,
  type NavSection,
} from "@lagoon/ui";

import { api, ApiError, type Entitlements } from "./api.js";
import { showAuditLink } from "./audit.js";
import { authErrorMessage, readAuthError } from "./auth-errors.js";
import { canSeeBilling, showBillingLink } from "./billing.js";
import { AuthErrorBanner } from "./components/auth-error-banner.js";
import { DunningBanner } from "./components/dunning-banner.js";
import { Failure, LoadingPanel, Working } from "./components/feedback.js";
import { OrgSwitcher } from "./components/org-switcher.js";
import { browserMemory, pickActiveOrg, type Org } from "./orgs.js";
import { routeInternalLink, useRouter, type Route } from "./router.js";
import { Audit } from "./screens/audit.js";
import { Groups } from "./screens/groups.js";
import { Help } from "./screens/help.js";
import { AcceptInvitation } from "./screens/invitation.js";
import { Members } from "./screens/members.js";
import { Settings } from "./screens/settings.js";
import { SignIn } from "./screens/sign-in.js";
import { SiteDetail } from "./screens/site-detail.js";
import { Billing } from "./screens/billing.js";
import { Sites } from "./screens/sites.js";

/**
 * The dashboard shell.
 *
 * Authentication is a query, not a flag: `/api/auth/me` is the single source of
 * truth, so a session revoked elsewhere surfaces on the next refetch rather
 * than leaving a stale signed-in UI.
 *
 * The active organisation is the second thing the shell owns, and it is
 * resolved the same way: `/api/orgs` is the list of organisations somebody is
 * actually in, `localStorage` holds nothing but a preference, and the preference
 * is re-checked against the list on every load. Somebody removed from a team
 * must not keep looking at it because their browser remembers the id, and the
 * server would refuse every request from that screen anyway.
 *
 * The chrome is `AppLayout` from `@lagoon/ui`: a rail on wide screens, a sheet
 * drawer on narrow ones, a skip link, real `nav` and `main` landmarks and
 * `aria-current` on the active item. What used to be a row of ghost buttons in
 * a top bar is now that navigation, which is the whole of the layout change
 * here; every route, request and permission rule below is unchanged.
 */
export function App(): React.ReactElement {
  const queryClient = useQueryClient();
  const { route, navigate } = useRouter();

  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: false,
  });

  // Config the server templated into the page, so hostnames are never guessed
  // by the client.
  const sitesDomain = readConfig("sitesDomain", "sites.blackpearl.app");
  const scheme = readConfig("scheme", "https");
  const branding = readBranding();
  // The identity providers sign-in may offer (LGN-169), as the platform
  // templated them in. Empty in every environment with one connection, which
  // is what keeps the sign-in screen a single button there.
  const connections = useMemo(() => parseConnections(readConfig("connections", "")), []);

  const signedIn = me.isSuccess;

  const orgs = useQuery({
    queryKey: ["orgs"],
    queryFn: async () => (await api.listOrgs()).orgs,
    // Nothing to ask about until there is a session. A signed-out visitor on
    // the invitation screen must not spend a request being told 401.
    enabled: signedIn,
  });

  const memory = useMemo(() => browserMemory(), []);
  const [chosenOrgId, setChosenOrgId] = useState<string | null>(() => memory.read());

  /**
   * The reason code the platform redirected back with (LGN-126).
   *
   * Read once, on the first render, because it describes the navigation that
   * created this page rather than anything the screen is currently doing. The
   * three `reauth_*` codes land here rather than on the sign-in screen: the
   * person is still signed in, and until now nothing in the shell looked at
   * `?error=` at all, so a re-authentication that failed looked like an action
   * that quietly did nothing.
   */
  const [authError, setAuthError] = useState<string | null>(() =>
    readAuthError(window.location.search),
  );

  const dismissAuthError = useCallback(() => {
    setAuthError(null);
    // Taken out of the address bar as well, so a refresh or a shared URL does
    // not bring back a message about a navigation that is long over.
    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const authErrorText = authErrorMessage(authError);

  const activeOrg: Org | undefined = useMemo(
    () => (orgs.data === undefined ? undefined : pickActiveOrg(orgs.data, chosenOrgId)),
    [orgs.data, chosenOrgId],
  );

  const selectOrg = useCallback(
    (orgId: string) => {
      memory.write(orgId);
      setChosenOrgId(orgId);
    },
    [memory],
  );

  const signOut = useCallback(() => {
    // Sign-out is best-effort on the client: the session row is what actually
    // matters, and it is revoked server-side.
    void api
      .signOut()
      .catch(() => {})
      .finally(() => {
        queryClient.clear();
        navigate("/");
      });
  }, [navigate, queryClient]);

  /**
   * The organisation's entitlements, for the dunning banner in the shell.
   *
   * In the shell and not on the billing screen, because somebody whose card has
   * failed is not on the billing screen: they are on the sites list wondering
   * why the upload button refused them.
   *
   * **Read from the entitlements route rather than from `GET /api/billing`
   * (LGN-97).** Billing is now `owner` and `billing` only, and the banner is
   * for everybody: an admin whose upload is refused needs to be told the
   * payment is why, and an admin is precisely who the billing screen is closed
   * to. The entitlements route is readable by every member and carries the same
   * four dunning fields from the same server-side function, so the banner says
   * one thing to a whole organisation. It is the same query key the create-site
   * form uses, so the sites screen costs no second request.
   *
   * No payment provider is touched behind it, so an outage cannot make the
   * dashboard hang. Refetched on a slow interval rather than a fast one: the
   * state changes on a webhook's schedule, which is minutes at best.
   */
  const activeOrgId = activeOrg?.id;
  const orgState = useQuery({
    queryKey: ["entitlements", activeOrgId],
    // Never called with the empty string: `enabled` is false until there is an
    // org, and the query key changes with it.
    queryFn: () => api.getEntitlements(activeOrgId ?? ""),
    enabled: signedIn && activeOrgId !== undefined,
    // A failure here must never take a screen down: the banner is additional
    // information, and its absence is the ordinary state of most organisations.
    retry: false,
    refetchInterval: 120_000,
  });

  /**
   * The invitation screen is reachable signed out, and is the only screen that
   * is. Everything else behind the auth gate below is somebody's data; this is
   * a link that arrived in an email, and the whole point of it is to get the
   * recipient signed in and back here.
   */
  if (route.name === "invitation") {
    return (
      <AcceptInvitation
        token={route.token}
        signedIn={signedIn}
        branding={branding}
        // The address, so the screen can say which account is about to accept
        // (LGN-131). This route renders before the shell, so nothing else on
        // the page names it. Undefined until `me` answers, which is the same
        // moment `signedIn` becomes true.
        email={me.data?.owner.email ?? null}
        navigate={navigate}
        onAccepted={(orgId) => {
          selectOrg(orgId);
          // Fire and forget: the switcher already points at the new org, and
          // the refetch is what makes its name appear rather than something
          // the acceptance screen is waiting on.
          void queryClient.invalidateQueries({ queryKey: ["orgs"] });
        }}
      />
    );
  }

  if (me.isPending) {
    return (
      <div className="lg:grid lg:min-h-dvh lg:place-items-center lg:bg-background">
        <Working label="Loading…" />
      </div>
    );
  }

  if (me.isError) {
    const unauthenticated = me.error instanceof ApiError && me.error.isUnauthenticated;
    if (!unauthenticated) {
      return (
        <div className="lg:grid lg:min-h-dvh lg:place-items-center lg:bg-background lg:px-6">
          <div className="lg:w-full lg:max-w-md">
            <Failure
              error={me.error}
              fallback={`Could not reach the ${branding.productName} API. Try again in a moment.`}
            />
          </div>
        </div>
      );
    }
    return <SignIn branding={branding} connections={connections} />;
  }

  const email = me.data.owner.email;

  return (
    <AppLayout
      sections={navigation(activeOrg, orgState.data)}
      activeId={activeId(route)}
      brand={<LagoonLockup product={branding.productName} />}
      // The library's navigation items are real anchors with no click hook, so
      // the shell finishes the job in the bubble phase. `Link` marks its own
      // clicks handled before this sees them; see `routeInternalLink`.
      onClick={(event) => {
        routeInternalLink(event, navigate);
      }}
      headerActions={
        <>
          {activeOrg === undefined || orgs.data === undefined ? null : (
            <OrgSwitcher orgs={orgs.data} active={activeOrg} onSelect={selectOrg} />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <UserRound />
                <span className="lg:sr-only">Account</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <span className="lg:block lg:text-subtle">Signed in as</span>
                <Mono className="lg:block lg:normal-case lg:text-strong">{email}</Mono>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  navigate("/settings");
                }}
              >
                <Settings2 />
                Account settings
              </DropdownMenuItem>
              <DropdownMenuItem destructive onSelect={signOut}>
                <LogOut />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
      sidebarFooter={
        <Mono className="lg:block lg:truncate lg:text-caption lg:text-subtle" title={email}>
          {email}
        </Mono>
      }
    >
      <div className="lg:mx-auto lg:flex lg:w-full lg:max-w-[1120px] lg:flex-col lg:gap-7">
        {/*
          Above every screen, not only Billing, and for every role. The whole
          value of a dunning banner is that it reaches somebody who has not gone
          looking for it, and the roles that cannot open the billing screen are
          the ones most likely to meet the refusal it explains. Only the link is
          gated: it is hidden on the billing screen, where it would point at the
          page already open, and from the roles it would send to a refusal.
        */}
        {authErrorText === null ? null : (
          <AuthErrorBanner message={authErrorText} onDismiss={dismissAuthError} />
        )}

        {orgState.data === undefined || activeOrg === undefined ? null : (
          <DunningBanner
            dunning={orgState.data.dunning}
            showLink={showBillingLink(activeOrg.role, route.name === "billing")}
            navigate={navigate}
          />
        )}

        {route.name === "settings" ? (
          <Settings />
        ) : route.name === "help" ? (
          <Help sitesDomain={sitesDomain} branding={branding} />
        ) : route.name === "groups" ? (
          <Groups />
        ) : route.name === "site" ? (
          <SiteDetail
            siteId={route.id}
            tab={route.tab}
            navigate={navigate}
            sitesDomain={sitesDomain}
            scheme={scheme}
            supportEmail={branding.supportEmail}
          />
        ) : activeOrg === undefined ? (
          // Both the sites list and the members screen are questions about one
          // organisation, so neither can be drawn before the switcher knows
          // which. This is the gap between the shell rendering and /api/orgs
          // answering, plus the error case, which is rare and short.
          <OrgsPending query={orgs} productName={branding.productName} />
        ) : route.name === "billing" ? (
          <Billing org={activeOrg} navigate={navigate} supportEmail={branding.supportEmail} />
        ) : route.name === "audit" ? (
          /*
             Keyed on the organisation, which is the LGN-125 lesson applied to
             a screen with a cursor rather than a filter: the paging state is a
             stack of `audit_events` ids belonging to one org, and carrying it
             across a switch would page the new org with the old one's cursor.
             The key throws the state away with the org it belonged to.
          */
          <Audit
            key={activeOrg.id}
            org={activeOrg}
            entitlements={orgState.data}
            productName={branding.productName}
          />
        ) : route.name === "members" ? (
          <Members
            org={activeOrg}
            navigate={navigate}
            meOwnerId={me.data.owner.id}
            onOrgCreated={selectOrg}
          />
        ) : (
          <Sites
            org={activeOrg}
            navigate={navigate}
            sitesDomain={sitesDomain}
            branding={branding}
          />
        )}
      </div>
    </AppLayout>
  );
}

/**
 * The rail, in two groups.
 *
 * The first is the work: sites, the groups attached to them, the people who
 * may manage them. The second is the account: billing where the role allows
 * it, the account's own settings, and the explanation of how any of it works.
 *
 * Billing is `owner` and `billing` only (LGN-97) and the item is simply absent
 * for anybody else, because offering a link certain to be refused is a small
 * lie. The platform decides the same thing again on the route itself.
 */
function navigation(
  activeOrg: Org | undefined,
  entitlements: Entitlements | undefined,
): readonly NavSection[] {
  const account: NavItem[] = [];
  if (activeOrg !== undefined && canSeeBilling(activeOrg.role)) {
    account.push({ id: "billing", label: "Billing", href: "/billing", icon: <CreditCard /> });
  }
  account.push({ id: "settings", label: "Settings", href: "/settings", icon: <Settings2 /> });
  account.push({ id: "help", label: "How it works", href: "/help", icon: <BookOpen /> });

  const work: NavItem[] = [
    { id: "sites", label: "Sites", href: "/", icon: <Globe /> },
    { id: "groups", label: "Groups", href: "/groups", icon: <UsersRound /> },
    { id: "members", label: "People", href: "/members", icon: <UserRound /> },
  ];
  /**
   * The audit trail, for the roles and the plans that have it (LGN-146).
   *
   * Absent rather than disabled when either gate is shut, for the same reason
   * Billing is: a rail item that leads to a refusal is a small lie, and one
   * that leads to an upsell for something the customer may already be paying
   * for is a worse one. `showAuditLink` returns false while the entitlements
   * query is still in flight, so the item appears once rather than appearing
   * and being taken away.
   */
  if (activeOrg !== undefined && showAuditLink(activeOrg.role, entitlements)) {
    work.push({ id: "audit", label: "Audit", href: "/audit", icon: <ScrollText /> });
  }

  return [
    { id: "work", items: work },
    { id: "account", label: "Account", items: account },
  ];
}

/** Which rail item is lit. A site's own page belongs to Sites. */
function activeId(route: Route): string {
  return route.name === "site" ? "sites" : route.name;
}

/** Waiting on, or failing to get, the list of organisations. */
function OrgsPending(props: {
  query: { isError: boolean; error: unknown };
  productName: string;
}): React.ReactElement {
  if (!props.query.isError) {
    return <LoadingPanel label="Loading organisations" />;
  }
  return (
    <Failure
      error={props.query.error}
      fallback={`Could not reach the ${props.productName} API. Try again in a moment.`}
    />
  );
}

/** Read a value the server injected as a data attribute on <body>. */
function readConfig(key: string, fallback: string): string {
  const value = document.body.dataset[key];
  return value === undefined || value === "" ? fallback : value;
}

/**
 * The product's name, support address and operator, as the server templated
 * them in.
 *
 * The fallback is the shared default rather than a literal typed here, so the
 * bundle holds no second copy of the name to go stale. It applies only to the
 * Vite dev server, which serves `index.html` without going through the
 * platform.
 */
function readBranding(): Branding {
  return {
    productName: readConfig("productName", DEFAULT_BRANDING.productName),
    supportEmail: readConfig("supportEmail", DEFAULT_BRANDING.supportEmail),
    legalEntity: readConfig("legalEntity", DEFAULT_BRANDING.legalEntity),
  };
}
